import { memo } from 'react'
import styled from 'styled-components'
import { breakpoints, colors, fontWeights } from '@/styles'
import Default from './default'
import FileImage from './file-image'
import FileSecure from './file-secure'
import Message from './message-text'
import SupportFile from './support-message-file'
import SupportImage from './support-message-image'
import SupportMessage from './support-message-text'

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 5px;
`
const MessageBubble = styled.div`
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
  display: flex;
  width: 100%;
  max-width: 360px;
  ${props =>
    props.isDrawerMessage &&
    `
    max-width: 100%;
  `}
  min-height: 43px;
  border-radius: 8px;
  overflow: hidden;
  word-break: break-word;
  word-wrap: break-word;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) and (min-width: ${breakpoints.ipadMiniMax}) {
    max-width: 270px;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    max-width: 270px;
  }
`

const LeftChat = styled(MessageBubble)`
  border-radius: 14px 14px 14px 5px;

  ${props =>
    props.middle &&
    `
    border-radius: 5px 14px 14px 5px;
  `}

  ${props =>
    props.last &&
    `
    border-radius: 5px 14px 14px 14px;
  `}
`

const RightChat = styled(MessageBubble)`
  border-radius: 14px 14px 5px 14px;

  ${props =>
    props.middle &&
    `
    border-radius: 14px 5px 5px 14px;
  `}

  ${props =>
    props.last &&
    `
    border-radius: 14px 5px 14px 14px;
  `}
`

const ChatBubbleWrapper = styled.div`
  display: flex;
  flex-direction: ${props => (props.isThem ? 'row' : 'row-reverse')};
  align-items: flex-end;
`

const SecondaryEvent = styled(Default)`
  width: 260px;
`

const StyledMessageBubble = styled(MessageBubble)`
  margin-top: 12px;
  max-width: 260px;
`

const EventsContainer = styled.div``

const DisplayNameText = styled.p`
  margin: 0 0 6px;
  max-width: 360px;
  font-size: 14px;
  font-weight: ${fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${props => (props.colour ? props.colour : colors.black)};
`

const Events = {
  chat: Message,
  chat_timeline_deleted: Message,
  chat_file_img: FileImage,
  chat_file_secure: FileSecure,
  chat_first_message: Message,
  chat_initial_message: Message,
  chat_auto_reply: Message,
  support_room_event_chat: SupportMessage,
  support_room_internal_note: SupportMessage,
  support_room_event_chat_bot_file_img: SupportImage,
  support_room_event_chat_bot_file_secure: SupportFile,
  listing_document_approval_accepted: SupportMessage,
  listing_document_approval_declined: SupportMessage,
  chat_payment_transaction_unresolved_underpaid: SupportMessage
}

const ChatEvent = ({
  event,
  isThem,
  translatedText,
  showUserName,
  previousMessage,
  nextMessage,
  showDrawer,
  isDrawerMessage,
  showChatName,
  linkPattern,
  colour
}) => {
  const Event = Events[event?.event_type] || Default
  const isChat = event?.event_type?.match(
    /^(chat|support_room_event_chat|chat_initial_message|chat_auto_reply)$/
  )
  const chatAndCardEvents = [
    'listing_document_approval_accepted',
    'listing_document_approval_declined',
    'chat_payment_transaction_unresolved_underpaid'
  ]
  const isChatAndCard = chatAndCardEvents.some(e => event?.event_type?.includes(e))
  const ChatBubble = isThem ? LeftChat : RightChat

  const firstMessage = !previousMessage
  const lastMessage = previousMessage && !nextMessage
  const middleMessage = nextMessage && previousMessage

  if (isChatAndCard) {
    return (
      <MessageContainer>
        {showUserName && <DisplayNameText colour={colour}>{event?.display_name}.</DisplayNameText>}
        <EventsContainer>
          <MessageBubble isDrawerMessage={isDrawerMessage}>
            <Event colour={colour} showUserName={showUserName} isThem={isThem} event={event} />
          </MessageBubble>
          <StyledMessageBubble>
            <SecondaryEvent isThem={isThem} event={event} />
          </StyledMessageBubble>
        </EventsContainer>
      </MessageContainer>
    )
  }

  const handleClick = () => {
    // Check for selected text. This allows user to highlight and
    // copy text without opening the message details drawer
    const hasSelectedText = window.getSelection()?.toString()?.length !== 0
    if (!isThem && !hasSelectedText) showDrawer()
  }

  return isChat ? (
    <>
      <ChatBubbleWrapper onClick={handleClick} isThem={isThem}>
        <ChatBubble last={lastMessage} middle={middleMessage}>
          <Event
            colour={colour}
            linkPattern={linkPattern}
            isDrawerMessage={isDrawerMessage}
            translatedText={translatedText}
            isThem={isThem}
            event={event}
            showChatName={showChatName && firstMessage}
            showUserName={showUserName}
          />
        </ChatBubble>
      </ChatBubbleWrapper>
    </>
  ) : (
    <MessageContainer>
      {showUserName && <DisplayNameText colour={colour}>{event?.display_name}.</DisplayNameText>}
      <MessageBubble>
        <Event linkPattern={linkPattern} isThem={isThem} event={event} />
      </MessageBubble>
    </MessageContainer>
  )
}

export default memo(ChatEvent)
